import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  EntityRefCell,
  ExportMenu,
  ListPageShell,
  PageHeader,
  SavedViewsBar,
  Table,
} from '~/components/data';
import { ItemPicker, WarehousePicker } from '~/components/domain';
import { DateRangePicker } from '~/components/primitives';
import { fetchStockLedger } from '~/lib/api/stock';
import { formatDecimal } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import shared from './_auth._shared.module.css';
import styles from './_auth.stock.ledger.module.css';

export const Route = createFileRoute('/_auth/stock/ledger')({
  staticData: { minMainWidth: 960 },
  component: StockLedger,
});

function StockLedger(): JSX.Element {
  const [filter, setFilter] = useState<{
    item?: string;
    warehouse?: string;
    date_from?: string;
    date_to?: string;
  }>({});

  const q = useQuery({
    queryKey: ['stock', 'ledger', filter],
    queryFn: () => fetchStockLedger(filter),
  });
  const state = useListPageState(q);

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_stock_ledger()}
        description={m.route_stock_ledger_description()}
        actions={<ExportMenu resource="stock-ledger" filters={filter} />}
      />
      <SavedViewsBar
        resource="stock-ledger"
        currentFilters={filter}
        onApply={(v) => setFilter(v.filters as typeof filter)}
      />
      {/* S-FIX-5-FE.5.3: sticky filter bar + active filter chips. */}
      <section className={[shared.sectionCard, styles.stickyFilters].join(' ')}>
        <h3 className={shared.sectionHeading}>{m.route_stock_filters_heading()}</h3>
        <div className={shared.formGrid4}>
          <ItemPicker
            label={m.route_stock_ledger_filter_item()}
            value={filter.item ?? ''}
            onChange={(v) => setFilter((f) => ({ ...f, item: v || undefined }))}
          />
          <WarehousePicker
            label={m.route_stock_ledger_filter_warehouse()}
            value={filter.warehouse ?? ''}
            onChange={(v) => setFilter((f) => ({ ...f, warehouse: v || undefined }))}
          />
          <DateRangePicker
            label={m.route_stock_ledger_filter_period()}
            value={{ from: filter.date_from ?? null, to: filter.date_to ?? null }}
            onChange={(r) =>
              setFilter((f) => ({
                ...f,
                date_from: r.from ?? undefined,
                date_to: r.to ?? undefined,
              }))
            }
          />
        </div>
        {(filter.item || filter.warehouse || filter.date_from || filter.date_to) && (
          <div className={styles.chipsRow} role="region" aria-label="Активные фильтры">
            {filter.item && (
              <span className={styles.chip}>
                {m.route_stock_ledger_filter_item()}: {filter.item.slice(0, 8)}
                <button
                  type="button"
                  className={styles.chipClose}
                  aria-label="Сбросить фильтр Item"
                  onClick={() => setFilter((f) => ({ ...f, item: undefined }))}
                >
                  ×
                </button>
              </span>
            )}
            {filter.warehouse && (
              <span className={styles.chip}>
                {m.route_stock_ledger_filter_warehouse()}: {filter.warehouse.slice(0, 8)}
                <button
                  type="button"
                  className={styles.chipClose}
                  aria-label="Сбросить фильтр Warehouse"
                  onClick={() => setFilter((f) => ({ ...f, warehouse: undefined }))}
                >
                  ×
                </button>
              </span>
            )}
            {(filter.date_from || filter.date_to) && (
              <span className={styles.chip}>
                {m.route_stock_ledger_filter_period()}: {filter.date_from ?? '…'} —{' '}
                {filter.date_to ?? '…'}
                <button
                  type="button"
                  className={styles.chipClose}
                  aria-label="Сбросить фильтр Period"
                  onClick={() =>
                    setFilter((f) => ({ ...f, date_from: undefined, date_to: undefined }))
                  }
                >
                  ×
                </button>
              </span>
            )}
            <button type="button" className={styles.clearAll} onClick={() => setFilter({})}>
              {m.ui_sbrosit_vse()}
            </button>
          </div>
        )}
      </section>
      <ListPageShell
        state={state}
        emptyTitle={m.route_stock_ledger_empty_title()}
        emptyDescription={m.route_stock_ledger_empty_description()}
      >
        {(rows) => (
          <Table
            ariaLabel={m.route_stock_ledger_aria()}
            data={rows}
            rowKey={(r) => r.id}
            stickyFirstColumn
            columns={[
              {
                id: 'at',
                header: m.route_stock_ledger_col_at(),
                sticky: true,
                accessor: (r) => r.posting_at.slice(0, 19).replace('T', ' '),
                mono: true,
              },
              {
                id: 'item',
                header: m.route_stock_col_item(),
                accessor: (r) => <EntityRefCell name={r.item_name} code={r.item_sku} />,
              },
              {
                id: 'wh',
                header: m.route_stock_col_warehouse(),
                accessor: (r) => <EntityRefCell name={r.warehouse_name} code={r.warehouse_code} />,
              },
              {
                id: 'batch',
                header: m.route_stock_col_batch(),
                accessor: (r) => {
                  if (!r.batch_no) return '—';
                  // FE-57: batch_qc_status + batch_expiry_date chip beside batch_no.
                  const qc = (r as { batch_qc_status?: string | null }).batch_qc_status;
                  const expiry = (r as { batch_expiry_date?: string | null }).batch_expiry_date;
                  const expired = expiry ? new Date(expiry) < new Date() : false;
                  const qcMap: Record<string, { bg: string; fg: string; label: string }> = {
                    approved: { bg: 'var(--ok-bg-15)', fg: 'var(--color-ok)', label: '✓' },
                    qc_passed: { bg: 'var(--ok-bg-15)', fg: 'var(--color-ok)', label: '✓' },
                    rejected: { bg: 'var(--danger-bg-15)', fg: 'var(--color-danger)', label: '×' },
                    qc_failed: { bg: 'var(--danger-bg-15)', fg: 'var(--color-danger)', label: '×' },
                    pending: { bg: 'var(--warn-bg-15)', fg: 'var(--color-warn)', label: '▲' },
                    qc_pending: { bg: 'var(--warn-bg-15)', fg: 'var(--color-warn)', label: '▲' },
                    quarantine: {
                      bg: 'var(--bg-surface)',
                      fg: 'var(--color-text-muted)',
                      label: '◆',
                    },
                  };
                  const qcStyle = qc ? qcMap[qc] : undefined;
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span className={shared.mono}>{r.batch_no}</span>
                      {qcStyle && (
                        <span
                          title={`QC: ${qc}`}
                          style={{
                            padding: '1px 5px',
                            fontSize: 10,
                            fontWeight: 600,
                            background: qcStyle.bg,
                            color: qcStyle.fg,
                            borderRadius: 3,
                          }}
                        >
                          {qcStyle.label}
                        </span>
                      )}
                      {expired && (
                        <span
                          title={`Просрочена: ${expiry}`}
                          style={{
                            padding: '1px 5px',
                            fontSize: 10,
                            fontWeight: 600,
                            background: 'var(--danger-bg-15)',
                            color: 'var(--color-danger)',
                            borderRadius: 3,
                          }}
                        >
                          ◆ exp
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                id: 'delta',
                header: m.route_stock_ledger_col_delta(),
                // FE-54: signed color + ▲/▼ icon для glance-read. inflow=ok, outflow=danger.
                // FE-214: formatDecimal обрезает trailing zeros.
                accessor: (r) => {
                  const isNeg = String(r.qty_delta).startsWith('-');
                  const isPos = !isNeg && String(r.qty_delta) !== '0';
                  const color = isNeg
                    ? 'var(--color-danger)'
                    : isPos
                      ? 'var(--color-ok)'
                      : 'var(--color-text-muted)';
                  const arrow = isNeg ? '▼' : isPos ? '▲' : '·';
                  return (
                    <span style={{ color }}>
                      {arrow} {formatDecimal(r.qty_delta)}
                    </span>
                  );
                },
                mono: true,
                align: 'right',
              },
              {
                id: 'after',
                header: m.route_stock_ledger_col_after(),
                accessor: (r) => formatDecimal(r.qty_after),
                mono: true,
                align: 'right',
              },
              {
                id: 'rate',
                header: m.route_stock_col_rate(),
                // FE-216: incoming_rate formatted с currency symbol через formatMoney (если содержит amount+currency).
                // Fallback: formatDecimal без currency. BE-side schema decides shape.
                accessor: (r) => formatDecimal(r.incoming_rate),
                mono: true,
                align: 'right',
              },
              {
                id: 'voucher',
                header: m.route_stock_ledger_col_voucher(),
                // S-FIX-5-FE.5.1: drill-down на originating документ.
                // BE возвращает voucher_link_target (BE.2.1) — путь к detail route.
                // Fallback на plain text если link_target=null (opening_stock/compensating).
                // FE-55 honest-audit: prefer business voucher_no (e.g. «SE-DRAFT-005»)
                // вместо raw type/id slice. BE может вернуть voucher_no если denorm доступен.
                accessor: (r) => {
                  const linkTarget = (r as { voucher_link_target?: string | null })
                    .voucher_link_target;
                  const voucherNo = (r as { voucher_no?: string | null }).voucher_no;
                  const label = voucherNo ?? `${r.voucher_type}/${r.voucher_id.slice(0, 8)}`;
                  if (!linkTarget) return label;
                  // TanStack Router typed paths — мапим из enum value в typed route.
                  switch (linkTarget) {
                    case '/stock-entries':
                      return (
                        <Link
                          to="/stock-entries/$id"
                          params={{ id: r.voucher_id }}
                          className={shared.link}
                        >
                          {label}
                        </Link>
                      );
                    case '/work-orders':
                      return (
                        <Link
                          to="/work-orders/$id"
                          params={{ id: r.voucher_id }}
                          className={shared.link}
                        >
                          {label}
                        </Link>
                      );
                    case '/landed-cost':
                      return (
                        <Link
                          to="/landed-cost/$id"
                          params={{ id: r.voucher_id }}
                          className={shared.link}
                        >
                          {label}
                        </Link>
                      );
                    case '/stock-reconciliation':
                      return (
                        <Link to="/stock-reconciliation" className={shared.link}>
                          {label}
                        </Link>
                      );
                    default:
                      return label;
                  }
                },
                mono: true,
              },
            ]}
          />
        )}
      </ListPageShell>
    </div>
  );
}
